import React, { useLayoutEffect, useRef, useState } from "react";
import useCentreFilterSection from "../../../Utils/useCentreFilterSection";

// Controls that make up a filter bar: sort buttons, period dropdowns and the
// State page's picker trigger.
const FILTER_CONTROL_SELECTOR = "button, select, .selection-drop";

/**
 * How wide the box has to be to end where the filters below it do — from the
 * input's left edge to the right edge of the furthest control that sits under
 * the box on the same header line, stopping at the sort bar's divider when it
 * has one. Null when there are no filters to match.
 */
/** The x part of an element's CSS `translate`, in px (0 when none). */
function translateX(el) {
  const value = el.style.translate || getComputedStyle(el).translate;
  if (!value || value === "none") return 0;
  return parseFloat(value) || 0;
}

/**
 * A control's rect with any sideways scroll of the bar undone — on a phone the
 * sort bar scrolls, and the box should not shrink as the reader scrolls it.
 * The centring shift (useCentreFilterSection) is undone too: the box and the
 * bar each get their own shift, and measuring across two different shifts
 * came up short, capped the bar narrower, and shrank it a little more on
 * every pass until the sorts stood two or three to a line.
 */
function unscrolledRect(el, stopAt) {
  const rect = el.getBoundingClientRect();
  let undone = -translateX(el);
  for (let node = el.parentElement; node && node !== stopAt; node = node.parentElement) {
    undone += node.scrollLeft - translateX(node);
  }
  return { left: rect.left + undone, right: rect.right + undone, top: rect.top, bottom: rect.bottom, width: rect.width, height: rect.height };
}

function measureFilterWidth(wrapper, endAtDivider = 1, endAtControl = null) {
  const parent = wrapper?.parentElement;
  const input = wrapper?.querySelector(".dashboard-detail-search-input");
  if (!parent || !input) return null;

  const inputRect = unscrolledRect(input, parent);

  // A page that names the control to end at stops at its right edge.
  if (endAtControl) {
    const control = parent.querySelector(endAtControl);
    if (control) {
      const width = Math.round(unscrolledRect(control, parent).right - inputRect.left);
      if (width > 0) return width;
    }
  }

  // Where the bar has a divider between its sorts and its member filters, the
  // box ends with the sorts — the filters past the divider are not matched.
  // endAtDivider picks a later divider for bars with more than one group.
  const dividerLefts = [];
  parent.querySelectorAll(".dashboard-detail-sort-divider").forEach((divider) => {
    const rect = unscrolledRect(divider, parent);
    if (rect.height === 0 || rect.top < inputRect.bottom - 1) return;
    dividerLefts.push(rect.left);
  });
  dividerLefts.sort((a, b) => a - b);
  const stopAt = dividerLefts[endAtDivider - 1] ?? Infinity;

  let right = 0;
  parent.querySelectorAll(FILTER_CONTROL_SELECTOR).forEach((control) => {
    if (wrapper.contains(control)) return;
    // An open dropdown's options are not part of the bar.
    if (control.closest('[role="listbox"], [role="menu"]')) return;
    const rect = unscrolledRect(control, parent);
    if (rect.width === 0 || rect.top < inputRect.bottom - 1) return;
    if (rect.right > stopAt) return;
    right = Math.max(right, rect.right);
  });

  const width = Math.round(right - inputRect.left);
  return width > 0 ? width : null;
}

/**
 * Name search on the dashboard detail lists. Takes its own line under the
 * heading at every width; the sort bar follows on the line below it. On a
 * desktop the box is one fixed width on every page (see App.css). The width
 * to the last filter under it is still measured into --dashboard-search-width
 * for the pages that match it on a phone.
 *
 * Plain text input rather than `type="search"`, so the clear control is the
 * page's own — the browser's built-in one is styled differently on every
 * engine. Escape clears the box too.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.ariaLabel]
 * @param {string} [props.className]
 * @param {number} [props.endAtDivider] — which of the bar's dividers the
 *   measured width stops at (1 = the first)
 * @param {string} [props.endAtControl] — selector for the control the box
 *   ends at instead, e.g. '[data-sort="oldest"]'
 * @param {boolean} [props.centreOverDivider=true] — slide the box and the
 *   filters under it to the report's centre (the column rule on a two-column
 *   report); off for pages that centre their own filter block
 * @param {boolean} [props.freezeCentre=false] — hold the box and filters where
 *   they are now instead of re-centring
 * @param {React.RefObject<HTMLElement>} [props.centreOnRef] — a one-column
 *   list narrower than the heading line: centre on it instead
 */
function DashboardDetailSearchBox({
  value,
  onChange,
  placeholder = "Search names",
  ariaLabel = "Search names",
  className = "",
  endAtDivider = 1,
  endAtControl = null,
  centreOverDivider = true,
  freezeCentre = false,
  centreOnRef,
}) {
  const hasValue = Boolean(value);
  const wrapperRef = useRef(null);
  const [filterWidth, setFilterWidth] = useState(null);

  useCentreFilterSection(wrapperRef, centreOverDivider, freezeCentre, centreOnRef);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const parent = wrapper?.parentElement;
    if (!parent) return undefined;

    // The width also goes on the header line, so the sort bar under the box
    // can match it (Birthdays this Month centres its member tabs under it).
    // The bar is capped at this width, so measure with the cap lifted: when a
    // label grows ("October" → "All Months") the end control wraps under the
    // old cap, measures narrower, and each pass shrinks the bar further until
    // every button stands on its own line.
    const measure = () => {
      parent.style.removeProperty("--dashboard-search-width");
      const width = measureFilterWidth(wrapper, endAtDivider, endAtControl);
      setFilterWidth(width);
      if (width) parent.style.setProperty("--dashboard-search-width", `${width}px`);
      else parent.style.removeProperty("--dashboard-search-width");
    };

    // Buttons change size as their labels do (a dropdown naming the month
    // picked), and come and go as the page loads, so watch every control on
    // the line and re-watch whenever the line's contents change.
    const ro = new ResizeObserver(measure);
    const observeControls = () => {
      ro.disconnect();
      ro.observe(parent);
      parent
        .querySelectorAll(FILTER_CONTROL_SELECTOR)
        .forEach((control) => ro.observe(control));
    };
    const mo = new MutationObserver(() => {
      observeControls();
      measure();
    });

    observeControls();
    measure();
    mo.observe(parent, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
      parent.style.removeProperty("--dashboard-search-width");
    };
  }, [endAtDivider, endAtControl]);

  return (
    <div
      ref={wrapperRef}
      className={`dashboard-detail-search${className ? ` ${className}` : ""}`}
      style={
        filterWidth
          ? { "--dashboard-search-width": `${filterWidth}px` }
          : undefined
      }
    >
      <svg
        className="dashboard-detail-search-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
      <input
        type="text"
        className="dashboard-detail-search-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && hasValue) {
            event.preventDefault();
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      {hasValue && (
        <button
          type="button"
          className="dashboard-detail-search-clear"
          onClick={() => onChange("")}
          aria-label="Clear search"
          title="Clear search"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * The empty-state line for a search that found nobody. Names the words that
 * came up empty, so the reader sees what to change instead of "no data".
 *
 * @param {object} props
 * @param {string} props.query
 * @param {string} [props.noun] — what the page lists, e.g. "birthdays"
 */
export function DashboardDetailSearchNoMatch({
  query,
  noun = "family members",
}) {
  return (
    <p>
      No {noun} match{" "}
      <span className="dashboard-detail-search-empty-query">
        “{String(query ?? "").trim()}”
      </span>
      .
    </p>
  );
}

export default DashboardDetailSearchBox;
